import axios, { AxiosInstance, AxiosError } from 'axios';
import { authService } from './authService';
import { getActiveTenantId } from './tenantContext';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080/api/v1';

/**
 * Axios instance with authentication interceptors
 * Automatically adds auth tokens and handles token refresh
 */
class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor: Add auth token
    this.client.interceptors.request.use(
      (config) => {
        const token = authService.getAccessToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        config.headers['x-tenant-id'] = getActiveTenantId();
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor: Handle token refresh
    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config;

        // If 401 and not already retried, try to refresh token
        if (error.response?.status === 401 && originalRequest && !originalRequest.headers['X-Retry']) {
          try {
            await authService.refreshToken();
            originalRequest.headers['X-Retry'] = 'true';
            return this.client(originalRequest);
          } catch (refreshError) {
            // Refresh failed, redirect to login
            window.location.href = '/login';
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );
  }

  getInstance(): AxiosInstance {
    return this.client;
  }
}

export const apiClient = new ApiClient().getInstance();

/**
 * Attaches the same 401 -> refresh-token -> retry-once behavior as apiClient
 * to any other axios instance. Several pages (dashboardService, Recommendations)
 * build their own axios instance directly instead of using apiClient, so they
 * previously had no way to recover from an expired access token short of a
 * full page reload.
 */
export function attachAuthRefresh(instance: AxiosInstance): AxiosInstance {
  instance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as any;

      // NEVER try to refresh the refresh call itself.
      //
      // authService.doRefreshToken() posts to Keycloak with the bare axios
      // module, and this interceptor is now attached to that module globally
      // (see main.tsx). Without this guard an expired REFRESH token produces a
      // 401 on the token endpoint, which re-enters here and calls
      // refreshToken() again - which, thanks to single-flight, hands back the
      // very promise that is in the middle of rejecting. Best case a confusing
      // cascade, worst case a hang, when the correct behaviour is simply "the
      // session is over, go to login".
      const url = String(originalRequest?.url || '');
      if (url.includes('/protocol/openid-connect/token')) {
        return Promise.reject(error);
      }

      if (error.response?.status === 401 && originalRequest && !originalRequest.headers?.['X-Retry']) {
        try {
          await authService.refreshToken();
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers['X-Retry'] = 'true';
          const token = authService.getAccessToken();
          if (token) {
            originalRequest.headers['Authorization'] = `Bearer ${token}`;
          }
          return instance(originalRequest);
        } catch (refreshError) {
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
      }

      return Promise.reject(error);
    }
  );

  return instance;
}
